import { useEffect, useState } from "react";
import { DocView } from "../../components/DocView";
import { Button, Card, ClevisMark, Input, Label } from "@clevis/shared-ui";
import { call } from "../../lib/callFunction";
import { SignaturePad } from "../../components/SignaturePad";

// Public e-sign page: the homeowner lands here from the emailed link
// (app.clevis.pro/sign/{tenantId}/{agreementId}?t={token}). Token-gated,
// no account needed — the pay-link pattern applied to documents.

interface PublicAgreement {
  companyName: string;
  logoUrl?: string | null;
  templateName: string;
  customerName: string;
  body: string;
  status: "pending" | "signed" | "void";
  signerName: string | null;
  signedAt: number | null;
}

export function SignAgreementPage({ tenantId, agreementId }: { tenantId: string; agreementId: string }) {
  const token = new URLSearchParams(window.location.search).get("t") ?? "";
  const [ag, setAg] = useState<PublicAgreement | null>(null);
  const [loadError, setLoadError] = useState("");
  const [step, setStep] = useState<"review" | "sign" | "done">("review");
  const [signerName, setSignerName] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    void call<PublicAgreement>("getAgreementPublic", { tenantId, agreementId, token }, { mutation: false })
      .then((res) => {
        setAg(res);
        setSignerName(res.customerName);
        if (res.status === "signed") setStep("done");
      })
      .catch((err) => setLoadError((err as Error).message));
  }, [tenantId, agreementId, token]);

  async function sign() {
    if (!signature) return;
    setBusy(true);
    setError("");
    try {
      await call(
        "signAgreement",
        { tenantId, agreementId, token, signerName: signerName.trim(), signaturePngBase64: signature },
        { mutation: false },
      );
      setStep("done");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full bg-cream">
      <div className="mx-auto max-w-lg p-4 pb-16">
        <div className="mb-4 flex items-center gap-2 pt-2">
          {ag?.logoUrl ? (
            <img src={ag.logoUrl} alt={ag.companyName} className="max-h-10 max-w-[160px] object-contain" />
          ) : (
            <>
              <ClevisMark size={24} />
              <span className="text-sm font-bold text-ink">{ag?.companyName ?? "Clevis"}</span>
            </>
          )}
        </div>

        {loadError ? (
          <Card>
            <p className="py-4 text-center text-sm text-steel">
              This signing link isn't valid anymore. Please ask the company to send a new one.
            </p>
          </Card>
        ) : null}

        {ag && ag.status === "void" ? (
          <Card>
            <p className="py-4 text-center text-sm text-steel">This document was withdrawn — nothing to sign.</p>
          </Card>
        ) : null}

        {ag && ag.status !== "void" && step === "review" ? (
          <>
            <DocView
              title={ag.templateName}
              body={ag.body}
              companyName={ag.companyName}
              logoUrl={ag.logoUrl ?? null}
              dateLabel={ag.customerName ? `Prepared for ${ag.customerName}` : undefined}
              requiresSignature={false}
              className="mb-4"
            />
            <Button className="w-full py-3 text-base" onClick={() => setStep("sign")}>
              Continue to sign
            </Button>
          </>
        ) : null}

        {ag && step === "sign" ? (
          <Card>
            <h2 className="mb-1 text-lg font-bold text-ink">{ag.templateName}</h2>
            <p className="mb-3 text-sm text-steel">By signing you agree to the terms you just reviewed.</p>
            <div className="mb-3">
              <Label>Your name</Label>
              <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Full name" autoComplete="name" />
            </div>
            <SignaturePad onChange={setSignature} />
            {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
            <div className="mt-4 flex gap-2">
              <Button variant="ghost" className="flex-1" onClick={() => setStep("review")}>
                Back
              </Button>
              <Button className="flex-1 py-3" onClick={() => void sign()} busy={busy} disabled={!signature || signerName.trim().length < 2}>
                Sign
              </Button>
            </div>
          </Card>
        ) : null}

        {ag && step === "done" ? (
          <Card className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-success/15 text-2xl">✓</div>
            <h2 className="mb-1 text-lg font-bold text-ink">
              {ag.status === "signed" && ag.signerName ? "Already signed" : "Signed — thank you!"}
            </h2>
            <p className="text-sm text-steel">
              {ag.templateName} {ag.signerName ? `— signed by ${ag.signerName}` : "is signed"}. {ag.companyName} has a copy on file.
            </p>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
